/**
 * D9 Phase 6: Cycle Evolution Panel
 * 
 * Shows TWO SEPARATE metrics between cycles:
 * 1. Personal Growth — Per-employee dimension delta between cycles
 * 2. Current Alignment Gap — Employee OFFER vs company_need_snapshot
 * 
 * These metrics are NEVER combined per D9 spec.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Target,
  Users,
  ArrowLeftRight,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Evolución entre Ciclos',
    subtitle: 'Comparación de crecimiento personal y alineación con la empresa',
    selectCycles: 'Seleccionar ciclos a comparar',
    cycleA: 'Ciclo Anterior',
    cycleB: 'Ciclo Actual',
    compare: 'Comparar',
    loading: 'Cargando...',
    noData: 'Se necesitan al menos 2 ciclos cerrados o activos para comparar evolución.',
    
    personalGrowth: 'Crecimiento Personal',
    personalGrowthDesc: '¿Cuánto cambió cada persona entre ciclos? Independiente del perfil empresa.',
    alignmentGap: 'Brecha de Alineación Actual',
    alignmentGapDesc: '¿Qué tan alineado está cada empleado con lo que la empresa necesita hoy?',
    
    dimension: 'Dimensión',
    avgDelta: 'Delta Promedio',
    improved: 'Mejoraron',
    declined: 'Bajaron',
    stable: 'Estable',
    avgGap: 'Brecha Promedio',
    aligned: 'Alineados',
    deficit: 'Déficit',
    exceeds: 'Excede',
    
    employee: 'Empleado',
    department: 'Departamento',
    netGrowth: 'Crecimiento Neto',
    absGap: 'Brecha Absoluta Prom.',
    improvedDims: 'Dims. Mejoraron',
    declinedDims: 'Dims. Bajaron',
    alignedDims: 'Dims. Alineadas',
    deficitDims: 'Dims. Déficit',
    
    employeesAnalyzed: 'empleados analizados',
    noCompanyNeed: 'Sin perfil de necesidad de empresa para este ciclo. La brecha de alineación no está disponible.',
    showDetails: 'Ver detalle',
    hideDetails: 'Ocultar detalle',
    
    trendUp: 'Mejora',
    trendDown: 'Baja',
    trendStable: 'Estable',
    statusAligned: 'Alineado',
    statusDeficit: 'Déficit',
    statusExceeds: 'Excede'
  },
  en: {
    title: 'Cycle Evolution',
    subtitle: 'Comparison of personal growth and alignment with the company',
    selectCycles: 'Select cycles to compare',
    cycleA: 'Previous Cycle',
    cycleB: 'Current Cycle',
    compare: 'Compare',
    loading: 'Loading...',
    noData: 'At least 2 closed or active cycles are needed to compare evolution.',
    
    personalGrowth: 'Personal Growth',
    personalGrowthDesc: 'How much did each person change between cycles? Independent of company profile.',
    alignmentGap: 'Current Alignment Gap',
    alignmentGapDesc: 'How aligned is each employee with what the company needs today?',
    
    dimension: 'Dimension',
    avgDelta: 'Avg Delta',
    improved: 'Improved',
    declined: 'Declined',
    stable: 'Stable',
    avgGap: 'Avg Gap',
    aligned: 'Aligned',
    deficit: 'Deficit',
    exceeds: 'Exceeds',
    
    employee: 'Employee',
    department: 'Department',
    netGrowth: 'Net Growth',
    absGap: 'Avg Absolute Gap',
    improvedDims: 'Improved Dims',
    declinedDims: 'Declined Dims',
    alignedDims: 'Aligned Dims',
    deficitDims: 'Deficit Dims',
    
    employeesAnalyzed: 'employees analyzed',
    noCompanyNeed: 'No company need profile for this cycle. Alignment gap is not available.',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    
    trendUp: 'Improved',
    trendDown: 'Declined',
    trendStable: 'Stable',
    statusAligned: 'Aligned',
    statusDeficit: 'Deficit',
    statusExceeds: 'Exceeds'
  }
};

const DeltaBar = ({ value, maxVal = 30 }) => {
  const pct = Math.min(100, (Math.abs(value) / maxVal) * 100);
  const isPositive = value > 0;
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="w-24 h-3 bg-slate-700 rounded-full overflow-hidden relative">
        {isPositive ? (
          <div
            className="absolute left-1/2 h-full bg-emerald-500 rounded-r-full"
            style={{ width: `${pct / 2}%` }}
          />
        ) : (
          <div
            className="absolute h-full bg-red-500 rounded-l-full"
            style={{ width: `${pct / 2}%`, right: '50%' }}
          />
        )}
        <div className="absolute left-1/2 top-0 w-px h-full bg-slate-500" />
      </div>
      <span className={`text-xs font-mono ${isPositive ? 'text-emerald-400' : value < 0 ? 'text-red-400' : 'text-slate-400'}`}>
        {value > 0 ? '+' : ''}{value.toFixed(1)}
      </span>
    </div>
  );
};

const GapBar = ({ gap, maxVal = 30 }) => {
  const absGap = Math.abs(gap);
  const pct = Math.min(100, (absGap / maxVal) * 100);
  const color = absGap <= 5 ? 'bg-emerald-500' : (gap < 0 ? 'bg-red-500' : 'bg-blue-500');
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="w-24 h-3 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-mono ${absGap <= 5 ? 'text-emerald-400' : gap < 0 ? 'text-red-400' : 'text-blue-400'}`}>
        {gap > 0 ? '+' : ''}{gap.toFixed(1)}
      </span>
    </div>
  );
};

const TrendBadge = ({ trend, t }) => {
  if (trend === 'up') return <Badge className="bg-emerald-900/50 text-emerald-400 text-xs border-0">{t.trendUp}</Badge>;
  if (trend === 'down') return <Badge className="bg-red-900/50 text-red-400 text-xs border-0">{t.trendDown}</Badge>;
  return <Badge className="bg-slate-700 text-slate-400 text-xs border-0">{t.trendStable}</Badge>;
};

const CycleEvolutionPanel = ({ empresaId }) => {
  const { language } = useLanguage();
  const t = TEXTS[language] || TEXTS.es;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cycleAId, setCycleAId] = useState('');
  const [cycleBId, setCycleBId] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState({});

  const fetchEvolution = useCallback(async (aId, bId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ lang: language });
      if (aId) params.append('cycle_a_id', aId);
      if (bId) params.append('cycle_b_id', bId);
      
      const res = await axios.get(`/v5/ciclos/${empresaId}/cycle-evolution?${params}`);
      if (res.data?.success) {
        setData(res.data.data);
        if (res.data.data.cycles) {
          setCycleAId(res.data.data.cycles.cycle_a.id);
          setCycleBId(res.data.data.cycles.cycle_b.id);
        }
      }
    } catch (err) {
      toast.error('Error loading evolution data');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [empresaId, language]);

  useEffect(() => {
    if (empresaId) fetchEvolution();
  }, [empresaId, fetchEvolution]);

  const toggleEmployee = (id) => {
    setExpandedEmployees(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-3 text-slate-400">{t.loading}</span>
      </div>
    );
  }

  if (!data || !data.has_comparison) {
    return (
      <Card className="bg-slate-800/80 border-slate-700">
        <CardContent className="p-8 text-center">
          <ArrowLeftRight className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">{data?.message?.[language] || t.noData}</p>
        </CardContent>
      </Card>
    );
  }

  const { cycles, summary, personal_growth_aggregate, alignment_gap_aggregate, employees, available_cycles } = data;
  const growthDims = Object.entries(personal_growth_aggregate || {});
  const gapDims = Object.entries(alignment_gap_aggregate || {});
  
  const employeesWithGrowth = employees?.filter(e => e.personal_growth) || [];
  const employeesWithGap = employees?.filter(e => e.alignment_gap) || [];

  return (
    <div data-testid="cycle-evolution-panel" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{t.title}</h2>
          <p className="text-sm text-slate-400">{t.subtitle}</p>
        </div>
        <Badge variant="outline" className="border-slate-600 text-slate-300">
          <Users className="w-3 h-3 mr-1" />
          {summary.employees_with_growth_data} {t.employeesAnalyzed}
        </Badge>
      </div>

      {/* Cycle Selector */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">{t.cycleA}:</label>
          <select
            data-testid="cycle-a-select"
            className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
            value={cycleAId}
            onChange={(e) => setCycleAId(e.target.value)}
          >
            {available_cycles?.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.estado})</option>
            ))}
          </select>
        </div>
        <ArrowLeftRight className="w-4 h-4 text-slate-500" />
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">{t.cycleB}:</label>
          <select
            data-testid="cycle-b-select"
            className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
            value={cycleBId}
            onChange={(e) => setCycleBId(e.target.value)}
          >
            {available_cycles?.map(c => (
              <option key={c.id} value={c.id}>{c.name} ({c.estado})</option>
            ))}
          </select>
        </div>
        <Button
          data-testid="compare-btn"
          size="sm"
          onClick={() => fetchEvolution(cycleAId, cycleBId)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          {t.compare}
        </Button>
      </div>

      {/* Two Separate Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Section 1: Personal Growth */}
        <Card className="bg-slate-800/80 border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <CardTitle className="text-lg text-white">{t.personalGrowth}</CardTitle>
            </div>
            <CardDescription className="text-slate-400 text-xs">{t.personalGrowthDesc}</CardDescription>
            <p className="text-xs text-slate-500 mt-1">
              {cycles.cycle_a.name} → {cycles.cycle_b.name}
            </p>
          </CardHeader>
          <CardContent>
            {growthDims.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 text-xs text-slate-500 pb-1 border-b border-slate-700">
                  <span>{t.dimension}</span>
                  <span className="w-32">{t.avgDelta}</span>
                  <span className="w-10 text-center">{t.improved}</span>
                  <span className="w-10 text-center">{t.declined}</span>
                  <span className="w-10 text-center">{t.stable}</span>
                </div>
                {growthDims
                  .sort((a, b) => Math.abs(b[1].avg_delta) - Math.abs(a[1].avg_delta))
                  .map(([dim, info]) => (
                  <div key={dim} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center text-sm">
                    <span className="text-slate-300">{info.label}</span>
                    <div className="w-32">
                      <DeltaBar value={info.avg_delta} />
                    </div>
                    <span className="w-10 text-center text-emerald-400 text-xs">{info.employees_improved}</span>
                    <span className="w-10 text-center text-red-400 text-xs">{info.employees_declined}</span>
                    <span className="w-10 text-center text-slate-500 text-xs">{info.employees_stable}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">{t.noData}</p>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Alignment Gap */}
        <Card className="bg-slate-800/80 border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <CardTitle className="text-lg text-white">{t.alignmentGap}</CardTitle>
            </div>
            <CardDescription className="text-slate-400 text-xs">{t.alignmentGapDesc}</CardDescription>
            <p className="text-xs text-slate-500 mt-1">
              {language === 'es' ? 'Empleado OFFER vs Necesidad Empresa' : 'Employee OFFER vs Company Need'} ({cycles.cycle_b.name})
            </p>
          </CardHeader>
          <CardContent>
            {!summary.has_company_need ? (
              <div className="bg-amber-900/20 border border-amber-700/30 rounded p-3">
                <p className="text-amber-400 text-xs">{t.noCompanyNeed}</p>
              </div>
            ) : gapDims.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 text-xs text-slate-500 pb-1 border-b border-slate-700">
                  <span>{t.dimension}</span>
                  <span className="w-32">{t.avgGap}</span>
                  <span className="w-10 text-center">{t.aligned}</span>
                  <span className="w-10 text-center">{t.deficit}</span>
                  <span className="w-10 text-center">{t.exceeds}</span>
                </div>
                {gapDims
                  .sort((a, b) => Math.abs(b[1].avg_gap) - Math.abs(a[1].avg_gap))
                  .map(([dim, info]) => (
                  <div key={dim} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center text-sm">
                    <span className="text-slate-300">{info.label}</span>
                    <div className="w-32">
                      <GapBar gap={info.avg_gap} />
                    </div>
                    <span className="w-10 text-center text-emerald-400 text-xs">{info.aligned}</span>
                    <span className="w-10 text-center text-red-400 text-xs">{info.deficit}</span>
                    <span className="w-10 text-center text-blue-400 text-xs">{info.exceeds}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">{t.noData}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Employee Details Table */}
      <Card className="bg-slate-800/80 border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-400" />
            <CardTitle className="text-base text-white">
              {language === 'es' ? 'Detalle por Empleado' : 'Employee Detail'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-slate-700">
                  <th className="text-left py-2 pr-4">{t.employee}</th>
                  <th className="text-left py-2 pr-4">{t.department}</th>
                  <th className="text-center py-2 px-2">{t.netGrowth}</th>
                  <th className="text-center py-2 px-2">{t.improvedDims}</th>
                  <th className="text-center py-2 px-2">{t.declinedDims}</th>
                  <th className="text-center py-2 px-2">{t.absGap}</th>
                  <th className="text-center py-2 px-2">{t.alignedDims}</th>
                  <th className="text-center py-2 px-2">{t.deficitDims}</th>
                  <th className="text-center py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {(employeesWithGrowth.length > 0 ? employeesWithGrowth : employeesWithGap).map(emp => (
                  <React.Fragment key={emp.id}>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 pr-4 text-white font-medium">{emp.nombre}</td>
                      <td className="py-2 pr-4 text-slate-400">{emp.departamento || '-'}</td>
                      <td className="py-2 px-2 text-center">
                        {emp.personal_growth ? (
                          <span className={`font-mono ${emp.personal_growth.net_growth > 0 ? 'text-emerald-400' : emp.personal_growth.net_growth < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                            {emp.personal_growth.net_growth > 0 ? '+' : ''}{emp.personal_growth.net_growth.toFixed(1)}
                          </span>
                        ) : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="py-2 px-2 text-center text-emerald-400">
                        {emp.personal_growth?.improved_count ?? '-'}
                      </td>
                      <td className="py-2 px-2 text-center text-red-400">
                        {emp.personal_growth?.declined_count ?? '-'}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {emp.alignment_gap ? (
                          <span className="font-mono text-slate-300">{emp.alignment_gap.avg_absolute_gap.toFixed(1)}</span>
                        ) : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="py-2 px-2 text-center text-emerald-400">
                        {emp.alignment_gap?.aligned_count ?? '-'}
                      </td>
                      <td className="py-2 px-2 text-center text-red-400">
                        {emp.alignment_gap?.deficit_count ?? '-'}
                      </td>
                      <td className="py-2 px-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleEmployee(emp.id)}
                          className="text-slate-400 hover:text-white h-6 px-2"
                        >
                          {expandedEmployees[emp.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </Button>
                      </td>
                    </tr>
                    {expandedEmployees[emp.id] && (
                      <tr>
                        <td colSpan={9} className="bg-slate-900/50 p-4">
                          <div className="grid grid-cols-2 gap-6">
                            {/* Growth details */}
                            {emp.personal_growth && (
                              <div>
                                <h4 className="text-xs text-emerald-400 font-medium mb-2 flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3" /> {t.personalGrowth}
                                </h4>
                                <div className="space-y-1">
                                  {Object.entries(emp.personal_growth.dimensions).map(([dim, info]) => (
                                    <div key={dim} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-400 capitalize">{dim}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-500">{info.cycle_a}</span>
                                        <span className="text-slate-600">→</span>
                                        <span className="text-slate-300">{info.cycle_b}</span>
                                        <span className={`font-mono w-12 text-right ${info.delta > 0 ? 'text-emerald-400' : info.delta < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                                          {info.delta > 0 ? '+' : ''}{info.delta}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Gap details */}
                            {emp.alignment_gap && (
                              <div>
                                <h4 className="text-xs text-blue-400 font-medium mb-2 flex items-center gap-1">
                                  <Target className="w-3 h-3" /> {t.alignmentGap}
                                </h4>
                                <div className="space-y-1">
                                  {Object.entries(emp.alignment_gap.dimensions).map(([dim, info]) => (
                                    <div key={dim} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-400 capitalize">{dim}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-slate-300">{info.employee_offer}</span>
                                        <span className="text-slate-600">vs</span>
                                        <span className="text-blue-400">{info.company_need}</span>
                                        <Badge
                                          className={`text-[10px] h-4 border-0 ${
                                            info.status === 'aligned' ? 'bg-emerald-900/50 text-emerald-400' :
                                            info.status === 'deficit' ? 'bg-red-900/50 text-red-400' :
                                            'bg-blue-900/50 text-blue-400'
                                          }`}
                                        >
                                          {info.gap > 0 ? '+' : ''}{info.gap}
                                        </Badge>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {employeesWithGrowth.length === 0 && employeesWithGap.length === 0 && (
            <p className="text-slate-500 text-sm text-center py-6">{t.noData}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CycleEvolutionPanel;
